/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingInfoDto, AutoLabelingJobType, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';
import { getAutoLabelingStepStatus } from 'src/customText/verticals/autotagTriggerModal/utils';
import { isGptFeatureDisabled, shouldRegionDisableGpt } from 'src/modules/gptPrediction/utils/gptUtils';

const messages = defineMessages({
    title: { id: 'customText.selectAutoLabelingJobType.title', defaultMessage: 'Select job type' },
    comingSoon: { id: 'customText.selectAutoLabelingJobType.comingSoon', defaultMessage: '(coming soon)' },
    learnMore: { id: 'customText.selectAutoLabelingJobType.learnMore', defaultMessage: 'Learn more about auto-labeling' },
    notSupportedRegion: {
        id: 'customText.selectAutoLabelingJobType.notSupportedRegion',
        defaultMessage: `The selected Language resource's region isn't supported. The supported regions are East US, South Central US and West Europe`
    },
    description: {
        id: 'customText.selectAutoLabelingJobType.description',
        defaultMessage: `Auto-labeling is a way to speed up labeling all of your files. Instead of labeling all your
        files manually, you can use machine learning to label them for you. You can select the files you want to label,
        which classes to label with and then review the auto-labeled data to check for accuracy.`
    },
    gptJobType: {
        id: 'customText.selectAutoLabelingJobType.gptJobType',
        defaultMessage: `Auto-label with GPT\nUse this when you want to speed up your labeling process. Get labels suggested by Azure OpenAI’s GPT technology. If you’ve already labeled some documents, those labels will be used to more accurately suggest labels. To label with GPT, you need to have an Azure OpenAI resource.`
    },
    trainedModelJobType: {
        id: 'customText.selectAutoLabelingJobType.trainedModelJobType',
        defaultMessage: `Auto-label based on a model you’ve trained {extraOption}\nUse this to label additional documents after you’ve already trained a model. That model will be used to auto-label more documents to improve accuracy`
    }
});

export type SelectAutoLabelingJobTypeProps = {
    onDismiss: () => void;
    autoLabelingInfoDto: AutoLabelingInfoDto;
    onSubmit: (selectedJobType: AutoLabelingJobType) => void;
};

const modalTheme = getScopedTheme('AutoLabelingModal');
const warningIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: '16px', color: modalTheme.warningColor }
});

export const SelectAutoLabelingJobType = observer((props: SelectAutoLabelingJobTypeProps) => {
    const { onDismiss, onSubmit, autoLabelingInfoDto } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [selectedJobType, setSelectedJobType] = React.useState(autoLabelingInfoDto.autoLabelingJobType);

    const { keyType } = rootStore.serviceKey;
    const { projectKind } = rootStore.currentCustomTextProjectStore;
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const { csAccountStore: currentLanguageResource } = rootStore.azureStore.session;

    return (
        <Stack grow verticalAlign="space-between">
            <Stack tokens={{ childrenGap: 20, padding: `0 0 5px ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>
                        {localize(messages.description)}
                        &nbsp;
                        <ExternalLink
                            href={
                                isExtractionProject
                                    ? 'https://aka.ms/customner-autolabeling'
                                    : 'https://aka.ms/customclassification-autolabeling'
                            }>
                            {localize(messages.learnMore)}
                        </ExternalLink>
                    </LuisText>
                </Stack>
                <ChoiceGroup
                    selectedKey={selectedJobType}
                    styles={{ root: { whiteSpace: 'pre-line' } }}
                    onChange={(_, option) => setSelectedJobType(option.key as AutoLabelingJobType)}
                    options={[
                        {
                            key: 'gpt',
                            text: localize(messages.gptJobType),
                            disabled: isGptFeatureDisabled(keyType, currentLanguageResource.region),
                            onRenderLabel: (labelProps, defaultLabelerRenderer) => (
                                <Stack tokens={{ childrenGap: 5 }}>
                                    {defaultLabelerRenderer(labelProps)}
                                    {shouldRegionDisableGpt(currentLanguageResource.region) && (
                                        <Stack horizontal tokens={{ childrenGap: 5, padding: '0 0 5px 26px' }}>
                                            <Icon iconName="Warning" styles={warningIconStyles} />
                                            <LuisText variant="small" color={modalTheme.secondaryTextColor}>
                                                {localize(messages.notSupportedRegion)}
                                            </LuisText>
                                        </Stack>
                                    )}
                                </Stack>
                            )
                        },
                        {
                            key: 'trainedModel',
                            disabled: !isExtractionProject,
                            text: localize(messages.trainedModelJobType, {
                                extraOption: isExtractionProject ? '' : localize(messages.comingSoon)
                            })
                        }
                    ]}
                />
            </Stack>

            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                onNext={() => onSubmit(selectedJobType)}
                activeStep={AutoLabelingSteps.AutoLabelingJob}
                isValid={
                    getAutoLabelingStepStatus(
                        AutoLabelingSteps.AutoLabelingJob,
                        AutoLabelingSteps.AutoLabelingJob,
                        { ...autoLabelingInfoDto, autoLabelingJobType: selectedJobType },
                        rootStore
                    ).isValid
                }
            />
        </Stack>
    );
});
